import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  Key,
  ShieldCheck,
  ArrowSquareOut,
  NumberCircleOne,
  NumberCircleTwo,
  NumberCircleThree,
  NumberCircleFour,
  NumberCircleFive,
  NumberCircleSix,
  Info,
  Warning,
} from "@phosphor-icons/react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

export default function OzonApiGuide() {
  const steps = [
    {
      number: 1,
      icon: NumberCircleOne,
      title: "Войдите в личный кабинет Ozon Seller",
      content: (
        <div className="space-y-3">
          <p>
            Перейдите на{" "}
            <a
              href="https://seller.ozon.ru"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1"
            >
              seller.ozon.ru
              <ArrowSquareOut size={14} />
            </a>{" "}
            и авторизуйтесь в своём аккаунте продавца.
          </p>
        </div>
      ),
    },
    {
      number: 2,
      icon: NumberCircleTwo,
      title: "Перейдите в раздел Seller API",
      content: (
        <div className="space-y-3">
          <p>В левом меню найдите:</p>
          <div className="bg-zinc-100 rounded-lg p-3 font-mono text-sm">
            Настройки → Seller API
          </div>
          <p className="text-sm text-zinc-600">
            Или воспользуйтесь прямой ссылкой:
          </p>
          <div className="flex items-center gap-2">
            <a
              href="https://seller.ozon.ru/app/settings/api-keys"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:text-blue-800 underline text-sm inline-flex items-center gap-1"
            >
              seller.ozon.ru/app/settings/api-keys
              <ArrowSquareOut size={14} />
            </a>
          </div>
        </div>
      ),
    },
    {
      number: 3,
      icon: NumberCircleThree,
      title: "Создайте новый Seller API ключ",
      content: (
        <div className="space-y-3">
          <p>Нажмите кнопку «Сгенерировать ключ» или «Создать ключ».</p>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Info size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">Выберите все типы токена</p>
                <p>
                  При создании ключа убедитесь, что выбраны все доступные типы токена 
                  (должно быть отмечено «Выбрано: все»). Это даст Tactic Seller полный доступ 
                  к данным вашего магазина.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <Warning size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-amber-800">
                <p className="font-medium">Важно!</p>
                <p>
                  Ключ показывается только один раз. Сразу скопируйте и сохраните
                  его в надёжном месте.
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      number: 4,
      icon: NumberCircleFour,
      title: "Скопируйте Client ID и API Key",
      content: (
        <div className="space-y-3">
          <p>После генерации вы получите два значения:</p>
          <div className="space-y-2">
            <div className="flex items-center justify-between bg-zinc-100 rounded-lg p-3">
              <div>
                <p className="text-sm font-medium text-zinc-700">Client ID</p>
                <p className="text-xs text-zinc-500">Числовой идентификатор (например: 123456)</p>
              </div>
              <Badge variant="outline">Обязательно</Badge>
            </div>
            <div className="flex items-center justify-between bg-zinc-100 rounded-lg p-3">
              <div>
                <p className="text-sm font-medium text-zinc-700">API Key</p>
                <p className="text-xs text-zinc-500">Строка формата UUID (например: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx)</p>
              </div>
              <Badge variant="outline">Обязательно</Badge>
            </div>
          </div>
        </div>
      ),
    },
    {
      number: 5,
      icon: NumberCircleFive,
      title: "Performance API (Реклама)",
      badge: "Опционально",
      content: (
        <div className="space-y-3">
          <p className="text-sm text-zinc-600">
            Если вы хотите видеть данные по рекламным кампаниям, вам понадобится
            отдельный Performance API ключ.
          </p>
          <div className="space-y-2">
            <p className="text-sm font-medium">Как получить:</p>
            <ol className="text-sm text-zinc-600 list-decimal list-inside space-y-1 ml-2">
              <li>
                Перейдите в{" "}
                <a
                  href="https://performance.ozon.ru"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline inline-flex items-center gap-1"
                >
                  performance.ozon.ru
                  <ArrowSquareOut size={12} />
                </a>
              </li>
              <li>Войдите в рекламный кабинет</li>
              <li>Перейдите в Настройки → Performance API</li>
              <li>Создайте новый ключ</li>
            </ol>
          </div>
          <p className="text-sm font-medium mt-4">После генерации вы получите:</p>
          <div className="space-y-2">
            <div className="flex items-center justify-between bg-zinc-100 rounded-lg p-3">
              <div>
                <p className="text-sm font-medium text-zinc-700">Performance Client ID</p>
                <p className="text-xs text-zinc-500">Строка (например: 12345678-1234567890123@advertising.performance.ozon.ru)</p>
              </div>
              <Badge variant="secondary">Опционально</Badge>
            </div>
            <div className="flex items-center justify-between bg-zinc-100 rounded-lg p-3">
              <div>
                <p className="text-sm font-medium text-zinc-700">Performance Client Secret</p>
                <p className="text-xs text-zinc-500">Строка (например: aBcDeFgHiJkLmNoPqRsTuVwXyZ)</p>
              </div>
              <Badge variant="secondary">Опционально</Badge>
            </div>
          </div>
        </div>
      ),
    },
    {
      number: 6,
      icon: NumberCircleSix,
      title: "Добавьте ключи в Tactic Seller",
      content: (
        <div className="space-y-3">
          <p>
            Перейдите в{" "}
            <Link to="/settings" className="text-blue-600 hover:text-blue-800 underline">
              Настройки
            </Link>{" "}
            → Маркетплейсы → Добавить и введите скопированные данные.
          </p>
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <ShieldCheck size={18} className="text-emerald-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-emerald-800">
                <p className="font-medium">Безопасность</p>
                <p>
                  Ваши ключи хранятся в зашифрованном виде (AES-256). Мы не имеем
                  доступа к ним в открытом виде.
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6 max-w-3xl" data-testid="ozon-api-guide-page">
      {/* Header with back button */}
      <div className="flex items-center gap-4">
        <Link to="/settings">
          <Button variant="ghost" size="sm" className="gap-2">
            <ArrowLeft size={18} />
            Назад к настройкам
          </Button>
        </Link>
      </div>

      {/* Title */}
      <div>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center">
            <Key size={22} weight="bold" className="text-white" />
          </div>
          <div>
            <h3 className="font-chivo font-bold text-2xl text-zinc-900">
              Подключение Ozon API
            </h3>
            <p className="text-sm text-zinc-500">
              Пошаговая инструкция по получению API-ключей
            </p>
          </div>
        </div>
      </div>

      {/* Info card */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Зачем нужны API-ключи?</p>
                <p>
                  API-ключи позволяют Tactic Seller получать данные о ваших товарах,
                  остатках, заказах и финансах напрямую из Ozon. Без них сервис
                  не сможет работать с вашим магазином.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Steps */}
      <div className="space-y-4">
        {steps.map((step, index) => (
          <motion.div
            key={step.number}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card data-testid={`guide-step-${step.number}`}>
              <CardHeader className="pb-2">
                <CardTitle className="font-chivo text-lg flex items-center gap-3">
                  <step.icon size={28} weight="fill" className="text-blue-500" />
                  <span>{step.title}</span>
                  {step.badge && (
                    <Badge variant="secondary" className="ml-2">{step.badge}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="pl-14">{step.content}</CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      <Separator />

      {/* FAQ Section */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <Card data-testid="faq-section">
          <CardHeader>
            <CardTitle className="font-chivo text-lg">Частые вопросы</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm font-medium text-zinc-900">
                Что делать, если я потерял ключ?
              </p>
              <p className="text-sm text-zinc-600">
                Создайте новый ключ в личном кабинете Ozon и обновите его в
                настройках Tactic Seller.
              </p>
            </div>
            <Separator />
            <div>
              <p className="text-sm font-medium text-zinc-900">
                Безопасно ли передавать API-ключи?
              </p>
              <p className="text-sm text-zinc-600">
                Да. API-ключи дают доступ только к данным магазина (товары,
                заказы, финансы). Они не позволяют изменять настройки магазина,
                выводить деньги или совершать покупки.
              </p>
            </div>
            <Separator />
            <div>
              <p className="text-sm font-medium text-zinc-900">
                Как отозвать доступ?
              </p>
              <p className="text-sm text-zinc-600">
                Удалите ключ в личном кабинете Ozon (Настройки → Seller API).
                После этого Tactic Seller потеряет доступ к вашему магазину.
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="flex justify-center pt-4 pb-8"
      >
        <Link to="/settings">
          <Button className="btn-ozon" size="lg" data-testid="go-to-settings-btn">
            Перейти к настройкам
          </Button>
        </Link>
      </motion.div>
    </div>
  );
}
